// src/components/study/ProtocolCompareButton.js
//
// The study page's Protocol versions card (admins only, flag PROTOCOL_COMPARE_ENABLED;
// 2026-10-22). Version 1, 2, 3 of the study's protocol: every upload asks "Is
// this a new version?" (once the study has a protocol); a yes makes it the
// current version. NOTHING happens automatically: the new version isn't read
// by the protocol reader or compared. "Compare Version N with Version N-1" is
// a button; it makes the AI Summary, the Redline (every changed word, found by
// code) and Check vs sponsor. Staff uploads sent as new versions wait for an admin.

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { GitCompare, X, Upload, RefreshCw, CheckCircle2, AlertTriangle, Loader2, ArrowLeft, Download, Send } from "lucide-react";
import { modalBackdrop, modalPanel } from "../motionPresets";
import { useDialog } from "../sourcex/useDialog";
import { listenProtocols, uploadAndCreateProtocol } from "../../services/protocolExtractionService";
import { listenVersions, listenVersionRequests, addVersion, declineVersionRequest, compareVersion, listenProtocolEvents } from "../../services/protocolVersionService";
import { currentVersion, newVersionQuestion, versionLabel, currentProtocolInfo, describeCurrentProtocol, nextVersionFor } from "../../services/protocolVersions";
import NewVersionDialog from "./NewVersionDialog";
import {
  listenComparisons,
  retryComparison,
  loadRedline,
  openComparisonPdf,
  runAiSummary,
  uploadAndCheck,
  loadCheckResult,
  downloadSummaryPdf,
  downloadRedlinePdf,
  shareComparison,
  listenComparison,
  MAX_PDF_BYTES,
} from "../../services/protocolCompareService";
import ProtocolRedlineView from "./ProtocolRedlineView";
import { PROTOCOL_SPONSOR_CHECK_ENABLED } from "../../config/featureFlags";
import AiHelperNotice from "../AiHelperNotice";

const BTN_PRIMARY =
  "protocol-compare-solid-btn inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-sm font-semibold text-white ring-1 ring-white/20 hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_SOFT =
  "protocol-compare-soft-btn inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs font-semibold text-slate-200 transition disabled:opacity-50 disabled:cursor-not-allowed";
const PANEL = "protocol-compare-panel rounded-xl border border-white/10 bg-white/[0.03]";

const STEPS = [
  { id: "reading", label: "Reading both PDFs" },
  { id: "comparing", label: "Finding every change" },
  { id: "summarizing", label: "Generating the AI summary" },
];
const CATEGORY_LABEL = {
  eligibility: "Eligibility",
  visits_schedule: "Visits & schedule",
  dosing_treatment: "Dosing & treatment",
  safety_reporting: "Safety reporting",
  procedures_assessments: "Procedures & assessments",
  endpoints_statistics: "Endpoints & statistics",
  administrative_editorial: "Administrative",
  other: "Other",
};
const STATUS_CHIP = {
  reading: ["Reading", "working"],
  comparing: ["Comparing", "working"],
  summarizing: ["Summarizing", "working"],
  done: ["Done", "done"],
  failed: ["Failed", "failed"],
};

const when = (ts) => {
  const d = ts?.toDate?.();
  return d ? d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "";
};
const studyKeyOf = (study) => String(study?.STUDY_ID || "").trim() || study?.id;
const versionName = (f) => String(f?.fileName || "").replace(/\.pdf$/i, "") || "the earlier version";

const Modal = ({ open, onClose, title, subtitle, children, footer, headerAction = null }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  // Into <body>: a glass card (backdrop-filter) traps position:fixed inside it.
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          {...modalBackdrop}
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 font-[Manrope]"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose?.();
          }}
        >
          <motion.div
            {...modalPanel}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className="protocol-compare-modal relative w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(124,58,237,0.6)] text-slate-100 flex flex-col"
          >
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-80 pointer-events-none" />
            <div className="relative px-5 py-3.5 border-b border-white/10 flex items-center gap-3 shrink-0">
              <div className="protocol-compare-icon w-9 h-9 rounded-xl bg-purple-500/15 border border-white/15 flex items-center justify-center shrink-0">
                <GitCompare className="w-4 h-4 text-purple-300" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-semibold text-white leading-tight truncate">{title}</h2>
                {subtitle && <div className="text-[11px] text-slate-400 truncate">{subtitle}</div>}
              </div>
              {headerAction}
              <button type="button" onClick={onClose} aria-label="Close" className="protocol-compare-soft-btn w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/10 flex items-center justify-center text-slate-300 shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="relative overflow-y-auto flex-1 p-4 sm:p-5">{children}</div>
            {footer && <div className="relative px-5 py-3 border-t border-white/10 flex flex-wrap items-center justify-end gap-2 shrink-0">{footer}</div>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

const StatusChip = ({ status }) => {
  const [label, tone] = STATUS_CHIP[status] || [status || "—", "working"];
  return <span className={`protocol-compare-chip protocol-compare-chip--${tone} text-[10px] px-1.5 py-0.5 rounded border font-semibold`}>{label}</span>;
};

const EDITORIAL_PAGE = 25;
const clip = (t, n = 140) => (t.length > n ? `${t.slice(0, n).trim()}…` : t);

/**
 * The changes the AI summary called editorial (wording, formatting,
 * renumbering), listed rather than only counted, so none is hidden from staff:
 * a sponsor can list some of these as changes (2026-10-22). Built from the
 * redline, so it costs nothing.
 */
const EditorialList = ({ ids, redline, onShowHunk }) => {
  const [shown, setShown] = useState(EDITORIAL_PAGE);
  const rows = useMemo(() => {
    if (!redline) return [];
    const at = new Map();
    for (const sec of redline.sections || []) for (const h of sec.hunks || []) at.set(h.id, { h, sec });
    return ids.map((id) => at.get(id)).filter(Boolean);
  }, [ids, redline]);
  if (!ids.length) return null;
  const name = (sec) => {
    const hd = sec.new || sec.old || {};
    return `${hd.number ? `${hd.number} ` : ""}${hd.title || ""}`.trim();
  };
  return (
    <section className={`${PANEL} p-3`}>
      <h4 className="text-sm font-semibold text-white">Editorial changes ({ids.length})</h4>
      <p className="text-[11px] text-slate-400 mt-0.5 mb-2">
        Changes the AI judged to be wording, formatting or renumbering only, so they aren&apos;t described above. Check them too: the sponsor may count some of these as changes.
      </p>
      {!redline && <div className="text-[12px] text-slate-400 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>}
      <ul className="divide-y divide-white/5">
        {rows.slice(0, shown).map(({ h, sec }) => (
          <li key={h.id} className="py-1.5 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-slate-400 min-w-0 truncate max-w-[40%]">
              {name(sec)}
              {h.newPage != null ? ` · p.${h.newPage}` : h.oldPage != null ? ` · old p.${h.oldPage}` : ""}
            </span>
            <span className="text-[12px] flex-1 min-w-0 break-words">
              {h.moved ? (
                <span className="text-slate-300">{h.words} words moved, unchanged</span>
              ) : (
                <>
                  {h.heading && <span className="text-slate-400">Heading: </span>}
                  {h.del && <del className="protocol-compare-del rounded px-0.5 mr-1 bg-rose-500/15 text-rose-200 line-through">{clip(h.del)}</del>}
                  {h.ins && <ins className="protocol-compare-ins rounded px-0.5 bg-emerald-500/15 text-emerald-200 underline">{clip(h.ins)}</ins>}
                </>
              )}
            </span>
            <button type="button" onClick={() => onShowHunk(h.id)} className={BTN_SOFT}>Show in redline</button>
          </li>
        ))}
      </ul>
      {rows.length > shown && (
        <div className="mt-2 flex justify-center">
          <button type="button" onClick={() => setShown((n) => n + EDITORIAL_PAGE)} className={BTN_SOFT}>Show more ({rows.length - shown} left)</button>
        </div>
      )}
    </section>
  );
};

/** The AI summary tab. */
const SummaryView = ({ study, studyKey, comparison, redline, onShowHunk, askConfirm, showAlert, readOnly = false }) => {
  const [cat, setCat] = useState("all");
  const [impact, setImpact] = useState("all");
  const [starting, setStarting] = useState(false);
  const s = comparison.summary;
  const status = comparison.summaryStatus;
  const write = async () => {
    const ok = await askConfirm(
      `Would you like to generate an AI summary of the changes from ${versionName(comparison.old)} to ${versionName(comparison.new)}?`,
      { title: s ? "Generate a new AI summary?" : "Generate AI summary?", confirmLabel: "Generate" }
    );
    if (!ok) return;
    setStarting(true);
    runAiSummary(studyKey, comparison.id)
      .catch((e) => showAlert(e.message))
      .finally(() => setStarting(false));
  };
  if (status === "none") return <div className={`${PANEL} p-4 text-sm text-slate-300`}>No differences were found, so there is nothing to summarize.</div>;
  if (status === "running" || (starting && !s)) {
    return (
      <div className={`${PANEL} p-4 text-sm text-slate-300 flex items-center gap-2`}>
        <Loader2 className="w-4 h-4 animate-spin text-purple-300" /> Generating the AI summary… You can close this window; it will keep going.
      </div>
    );
  }
  if (!s) {
    return (
      <div className={`${PANEL} p-4 space-y-3`}>
        {status === "failed" && comparison.summaryError && (
          <div className="protocol-compare-error rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{comparison.summaryError}</div>
        )}
        <p className="text-sm text-slate-300">{readOnly ? "No AI summary was made for this comparison. The Redline tab shows every change." : "No AI summary yet."}</p>
        {!readOnly && <button type="button" onClick={write} disabled={starting} className={BTN_PRIMARY}>Generate AI summary</button>}
      </div>
    );
  }
  const changes = (s.changes || []).filter((c) => (cat === "all" || c.category === cat) && (impact === "all" || c.impact === impact));
  const cats = [...new Set((s.changes || []).map((c) => c.category))];
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button type="button" onClick={() => downloadSummaryPdf(study, comparison)} className={BTN_SOFT}>
          <Download className="w-3.5 h-3.5" /> Download summary PDF
        </button>
      </div>
      <AiHelperNotice>This summary was written by AI. Check each change in the redline before acting on it.</AiHelperNotice>
      {comparison.summaryError && <div className="protocol-compare-error rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{comparison.summaryError}</div>}
      {s.overview && <div className={`${PANEL} p-3 text-sm text-slate-200 whitespace-pre-wrap`}>{s.overview}</div>}
      {(s.changes || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {["all", ...cats].map((c) => (
            <button key={c} type="button" onClick={() => setCat(c)} aria-pressed={cat === c} className={`protocol-compare-filter ${cat === c ? "protocol-compare-filter--on" : ""} text-[11px] px-2 py-1 rounded-lg border border-white/10 ${cat === c ? "bg-purple-500/20 text-white" : "bg-white/[0.03] text-slate-300"}`}>
              {c === "all" ? "All" : CATEGORY_LABEL[c] || c}
            </button>
          ))}
          <span className="w-2" />
          {["all", "high", "medium", "low"].map((i) => (
            <button key={i} type="button" onClick={() => setImpact(i)} aria-pressed={impact === i} className={`protocol-compare-filter ${impact === i ? "protocol-compare-filter--on" : ""} text-[11px] px-2 py-1 rounded-lg border border-white/10 ${impact === i ? "bg-purple-500/20 text-white" : "bg-white/[0.03] text-slate-300"}`}>
              {i === "all" ? "Any impact" : `${i[0].toUpperCase()}${i.slice(1)} impact`}
            </button>
          ))}
        </div>
      )}
      {changes.map((c, i) => (
        <article key={`${c.title}-${i}`} className={`${PANEL} p-3`}>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h4 className="text-sm font-semibold text-white">{c.title || "Change"}</h4>
            <span className={`protocol-compare-chip protocol-compare-chip--impact-${c.impact} text-[10px] px-1.5 py-0.5 rounded border font-semibold`}>{c.impact} impact</span>
            <span className="text-[11px] text-slate-400">{CATEGORY_LABEL[c.category] || c.category}{c.section ? ` · ${c.section}` : ""}</span>
          </div>
          {(c.before || c.after) && (
            <div className="grid sm:grid-cols-2 gap-2 text-[12px] my-2">
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Before</div><div className="text-slate-300">{c.before || "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Now</div><div className="text-slate-200">{c.after || "—"}</div></div>
            </div>
          )}
          {c.siteAction && <div className="text-[12px] text-slate-200"><span className="text-slate-400">What the site needs to do: </span>{c.siteAction}</div>}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {c.hunkIds?.length > 0 && (
              <button type="button" onClick={() => onShowHunk(c.hunkIds[0])} className={BTN_SOFT}>Show in redline</button>
            )}
          </div>
        </article>
      ))}
      {(s.unexplainedHunkIds || []).length > 0 && (
        <div className="text-[11px] text-slate-400">{s.unexplainedHunkIds.length} change(s) weren&apos;t covered by the summary. See the redline.</div>
      )}
      <EditorialList ids={s.editorialHunkIds || []} redline={redline} onShowHunk={onShowHunk} />
      {!readOnly && (
        <div className="flex justify-end">
          <button type="button" onClick={write} disabled={starting} className={BTN_SOFT}>Generate a new summary</button>
        </div>
      )}
    </div>
  );
};

const OUTCOMES = [
  { id: "notFound", label: "Not found", hint: "not found in the redline", tone: "failed" },
  { id: "redlineOnly", label: "Found, not in the summary", hint: "in the redline, but not in the AI summary", tone: "changed" },
  { id: "explained", label: "Found and summarized", hint: "in the redline and the AI summary", tone: "done" },
];

/** The "Check vs sponsor" tab: which changes on the sponsor's own summary of changes were found. */
const CheckView = ({ studyKey, comparison, onShowHunk, askConfirm, showAlert }) => {
  const [file, setFile] = useState(null);
  const [starting, setStarting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const c = comparison.check;
  const sum = comparison.summary;
  const [result, setResult] = useState(null);
  const [resultError, setResultError] = useState("");
  const resultKey = c && c.status === "done" ? c.resultPath : null;
  useEffect(() => {
    let cancelled = false;
    setResult(null);
    setResultError("");
    if (resultKey) {
      loadCheckResult(studyKey, comparison.id)
        .then((r) => !cancelled && setResult(r))
        .catch((e) => !cancelled && setResultError(e.message || "Couldn't load the check. Please try again."));
    }
    return () => {
      cancelled = true;
    };
  }, [studyKey, comparison.id, resultKey]);
  const summaryReady = sum && ["done", "partial"].includes(comparison.summaryStatus);

  const run = async () => {
    if (!file) return showAlert("Choose the sponsor's summary of changes (PDF).");
    const ok = await askConfirm(
      `Would you like to check ${versionName({ fileName: file.name })} against the changes found from ${versionName(comparison.old)} to ${versionName(comparison.new)}?`,
      { title: "Check against the sponsor's summary?", confirmLabel: "Check" }
    );
    if (!ok) return;
    setStarting(true);
    try {
      const { done } = await uploadAndCheck(studyKey, comparison.id, file);
      setShowForm(false);
      setFile(null);
      done.catch((e) => showAlert(e.message)).finally(() => setStarting(false));
    } catch (e) {
      showAlert(e.message);
      setStarting(false);
    }
  };

  if (!summaryReady) {
    return (
      <div className={`${PANEL} p-4 text-sm text-slate-300`}>
        Generate the AI summary first, on the Summary tab.
      </div>
    );
  }
  const form = (
    <div className={`${PANEL} p-4 space-y-3`}>
      <p className="text-[12px] text-slate-400">Upload the sponsor&apos;s summary of changes to see which of the changes it lists were found.</p>
      <div>
        <label htmlFor="pc-soc" className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
          Sponsor&apos;s summary of changes (PDF)
        </label>
        <input id="pc-soc" type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm text-slate-300" />
      </div>
      <button type="button" onClick={run} disabled={starting} className={BTN_PRIMARY}>
        {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Check
      </button>
    </div>
  );
  if (c && c.status === "running") {
    return (
      <div className={`${PANEL} p-4 text-sm text-slate-300 flex items-center gap-2`}>
        <Loader2 className="w-4 h-4 animate-spin text-purple-300" /> Checking against {c.fileName || "the sponsor's summary"}… You can close this window; it will keep going.
      </div>
    );
  }
  if (!c || c.status !== "done") {
    return (
      <div className="space-y-3">
        {c && c.status === "failed" && (
          <div className="protocol-compare-error rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
            The check didn&apos;t finish: {c.errorMessage || "unknown error"}
          </div>
        )}
        {form}
      </div>
    );
  }

  const n = c.counts || {};
  const extras = (result && result.extraSummaryIndexes) || c.extraSummaryIndexes || [];
  const titleOf = (i) => (sum.changes && sum.changes[i] ? sum.changes[i].title : `Item ${i + 1}`);
  const tiles = [
    ["Listed by the sponsor", n.sponsorItems, ""],
    ["Found and summarized", n.explained, "done"],
    ["Found, not in the summary", n.redlineOnly, "changed"],
    ["Not found", n.notFound, "failed"],
    ["Other changes found", n.extra, "working"],
  ];
  return (
    <div className="space-y-3">
      <div className="protocol-compare-warn rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 flex gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0" /> AI did the matching and can make mistakes. Use &ldquo;Show in redline&rdquo; to check a match.
      </div>
      <div className="text-[11px] text-slate-400">
        Checked against {c.fileName || "the sponsor's summary"}
        {c.by ? ` by ${c.by}` : ""}
        {c.finishedAt ? ` on ${when(c.finishedAt)}` : ""}.
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {tiles.map(([label, v, tone]) => (
          <div key={label} className={`${PANEL} p-3`}>
            <div className="text-xl font-semibold text-white">{v ?? 0}</div>
            <div
              className={`text-[11px] ${
                tone ? `protocol-compare-chip protocol-compare-chip--${tone} inline-block px-1.5 py-0.5 rounded border mt-1` : "text-slate-400"
              }`}
            >
              {label}
            </div>
          </div>
        ))}
      </div>
      {resultError && <div className="protocol-compare-error rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{resultError}</div>}
      {c.resultPath && !result && !resultError && (
        <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading the list…</div>
      )}
      {OUTCOMES.map((o) => {
        const list = ((result && result.items) || c.items || []).filter((it) => it.outcome === o.id);
        if (!list.length) return null;
        return (
          <section key={o.id}>
            <h4 className="text-sm font-semibold text-white mb-1">
              {o.label} <span className="text-[11px] font-normal text-slate-400">· {o.hint}</span>
            </h4>
            <ul className="space-y-2">
              {list.map((it, i) => (
                <li key={`${o.id}-${i}`} className={`${PANEL} p-3`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`protocol-compare-chip protocol-compare-chip--${o.tone} text-[10px] px-1.5 py-0.5 rounded border font-semibold`}>{o.label}</span>
                    {it.section && <span className="text-[11px] text-slate-400">{it.section}</span>}
                  </div>
                  <div className="text-[13px] text-slate-200 mt-1">{it.item}</div>
                  {it.note && <div className="text-[11px] text-slate-400 mt-1">{it.note}</div>}
                  {it.summaryIndexes?.length > 0 && (
                    <div className="text-[11px] text-slate-400 mt-1">In the summary as: {it.summaryIndexes.map(titleOf).join("; ")}</div>
                  )}
                  {it.hunkIds?.length > 0 && (
                    <button type="button" onClick={() => onShowHunk(it.hunkIds[0])} className={`${BTN_SOFT} mt-2`}>
                      Show in redline
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
      {extras.length > 0 && (
        <section>
          <h4 className="text-sm font-semibold text-white mb-1">
            Other changes found <span className="text-[11px] font-normal text-slate-400">· in the AI summary, not on the sponsor&apos;s list</span>
          </h4>
          <ul className="space-y-1">
            {extras.map((i) => (
              <li key={i} className={`${PANEL} p-2 flex flex-wrap items-center gap-2`}>
                <span className="text-[12px] text-slate-200 flex-1">{titleOf(i)}</span>
                {sum.changes?.[i]?.hunkIds?.length > 0 && (
                  <button type="button" onClick={() => onShowHunk(sum.changes[i].hunkIds[0])} className={BTN_SOFT}>
                    Show in redline
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {showForm ? (
        form
      ) : (
        <div className="flex justify-end">
          <button type="button" onClick={() => setShowForm(true)} className={BTN_SOFT}>
            Check another file
          </button>
        </div>
      )}
    </div>
  );
};

/** One comparison: progress while it runs, then Summary | Redline | Check vs sponsor. */
const ResultView = ({ study, studyKey, comparison, onBack, askConfirm, showAlert, readOnly = false }) => {
  const [tab, setTab] = useState("redline");
  const [redline, setRedline] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [focusHunk, setFocusHunk] = useState(null);

  useEffect(() => {
    if (comparison.summaryStatus && comparison.summaryStatus !== "off" && comparison.summary) setTab("summary");
  }, [comparison.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    if (comparison.status === "done" && comparison.redlinePath && !redline) {
      loadRedline(studyKey, comparison.id).then((r) => !cancelled && setRedline(r)).catch((e) => !cancelled && setLoadError(e.message || "Couldn't load the redline. Please try again."));
    }
    return () => {
      cancelled = true;
    };
  }, [studyKey, comparison.id, comparison.status, comparison.redlinePath, redline]);

  const showHunk = (h) => {
    setFocusHunk(h);
    setTab("redline");
  };
  const open = (which) => openComparisonPdf(studyKey, comparison.id, which).catch((e) => showAlert(e.message));
  const st = comparison.stats || {};
  const working = ["reading", "comparing", "summarizing"].includes(comparison.status);

  return (
    <div className="space-y-3">
      {!readOnly && <button type="button" onClick={onBack} className={BTN_SOFT}><ArrowLeft className="w-3.5 h-3.5" /> All versions</button>}
      <div className={`${PANEL} p-3 text-[12px] text-slate-300 grid sm:grid-cols-2 gap-2`}>
        <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{comparison.old?.version ? `Version ${comparison.old.version}` : "Old"}</div>{comparison.old?.fileName || "—"}{comparison.old?.pageCount ? ` · ${comparison.old.pageCount} pages` : ""}</div>
        <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{comparison.new?.version ? `Version ${comparison.new.version}` : "New"}</div>{comparison.new?.fileName || "—"}{comparison.new?.pageCount ? ` · ${comparison.new.pageCount} pages` : ""}</div>
        <div className="sm:col-span-2 text-[11px] text-slate-500">Started {when(comparison.createdAt)} by {comparison.createdBy || "—"} · <StatusChip status={comparison.status} /></div>
      </div>

      {working && (
        <div className={`${PANEL} p-4`}>
          <ol className="space-y-1.5">
            {STEPS.map((s) => {
              const idx = STEPS.findIndex((x) => x.id === comparison.status);
              const mine = STEPS.findIndex((x) => x.id === s.id);
              return (
                <li key={s.id} className="flex items-center gap-2 text-sm text-slate-300">
                  {mine < idx ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : mine === idx ? <Loader2 className="w-4 h-4 animate-spin text-purple-300" /> : <span className="w-4 h-4 inline-block rounded-full border border-white/20" />}
                  {s.label}
                </li>
              );
            })}
          </ol>
          <p className="mt-3 text-[11px] text-slate-400">You can close this window; it will keep going. The result appears in the list.</p>
        </div>
      )}

      {comparison.status === "failed" && (
        <div className="protocol-compare-error rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200 flex flex-wrap items-center gap-2">
          <span className="flex-1">The comparison failed: {comparison.errorMessage || "unknown error"}</span>
          {!readOnly && <button type="button" onClick={() => retryComparison(studyKey, comparison.id).catch((e) => showAlert(e.message))} className={BTN_SOFT}><RefreshCw className="w-3.5 h-3.5" /> Try again</button>}
        </div>
      )}

      {comparison.status === "done" && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div role="tablist" className="inline-flex rounded-xl border border-white/10 overflow-hidden">
              {["summary", "redline", ...(PROTOCOL_SPONSOR_CHECK_ENABLED && !readOnly ? ["check"] : [])].map((t) => (
                <button key={t} role="tab" type="button" aria-selected={tab === t} onClick={() => setTab(t)} className={`protocol-compare-tab ${tab === t ? "protocol-compare-tab--on bg-purple-500/20 text-white" : "bg-white/[0.03] text-slate-300"} px-3 py-1.5 text-xs font-semibold`}>
                  {t === "summary" ? "Summary" : t === "redline" ? "Redline" : "Check vs sponsor"}
                </button>
              ))}
            </div>
            <span className="text-[11px] text-slate-400">
              {st.hunks || 0} change(s){st.tableHunks ? ` · ${st.tableHunks} on table pages` : ""}{st.ocrHunks ? ` · ${st.ocrHunks} from scanned areas` : ""}
            </span>
          </div>
          {tab === "summary" && <SummaryView readOnly={readOnly} study={study} studyKey={studyKey} comparison={comparison} redline={redline} onShowHunk={showHunk} askConfirm={askConfirm} showAlert={showAlert} />}
          {tab === "check" && PROTOCOL_SPONSOR_CHECK_ENABLED && !readOnly && <CheckView studyKey={studyKey} comparison={comparison} onShowHunk={showHunk} askConfirm={askConfirm} showAlert={showAlert} />}
          {tab === "redline" && (
            <div>
              {loadError && <div className="protocol-compare-error rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{loadError}</div>}
              {!redline && !loadError && <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading the redline…</div>}
              {redline && (
                <ProtocolRedlineView
                  redline={redline}
                  focusHunkId={focusHunk}
                  onOpenOld={() => open("old")}
                  onOpenNew={() => open("new")}
                  onDownload={(onlyChanged) => downloadRedlinePdf(study, comparison, redline, onlyChanged)}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const PROTOCOL_STATUS_LABEL = { uploaded: "Not read", processing: "Being read", needs_review: "Needs review", approved: "Approved", failed: "Reading failed" };

/** Every protocol uploaded on this study (study page and SourceX), newest first. */
const UploadsList = ({ protocols, versions, requests }) => (
  <ul className="space-y-2">
    {(protocols || []).map((p) => (
      <li key={p.id} className={`${PANEL} p-3`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-white font-semibold">{versionLabel(p, versions, requests) || "No version"}</span>
          <span className="text-[12px] text-slate-300 truncate">{p.fileName || p.id}</span>
          <span className="protocol-compare-chip text-[10px] px-1.5 py-0.5 rounded border font-semibold">{PROTOCOL_STATUS_LABEL[p.status] || p.status}</span>
        </div>
        <div className="text-[11px] text-slate-400 mt-0.5 flex flex-wrap items-center gap-2">
          <span>Uploaded{p.createdBy ? ` by ${p.createdBy}` : ""}{p.uploadedAt ? ` · ${when(p.uploadedAt)}` : ""}</span>
          {p.downloadUrl && (
            <a href={p.downloadUrl} target="_blank" rel="noreferrer" className="underline text-slate-300 hover:text-white">Open PDF</a>
          )}
        </div>
      </li>
    ))}
  </ul>
);

const EVENT_TEXT = {
  version1_recorded: (e) => `marked Version 1 (the protocol in use)${e.detail ? `: ${e.detail}` : ""}`,
  version_added: (e) => `added Version ${e.version}${e.detail ? `: ${e.detail}` : ""}`,
  request_approved: (e) => `approved Version ${e.version}${e.detail ? `: ${e.detail}` : ""}`,
  request_sent: (e) => `sent a protocol as a new version${e.detail ? `: ${e.detail}` : ""}`,
  request_declined: (e) => `said it isn't a new version${e.detail ? `: ${e.detail}` : ""}`,
  compare_started: (e) => `compared ${e.detail || `Version ${e.version}`}`,
  compare_done: (e) => `comparison of Version ${e.version} finished${e.detail ? ` (${e.detail})` : ""}`,
  compare_failed: (e) => `comparison of Version ${e.version} didn't finish`,
  summary_generated: (e) => `generated the AI summary${e.version ? ` for Version ${e.version}` : ""}${e.detail ? ` (${e.detail})` : ""}`,
  comparison_shared: (e) => `sent the comparison${e.detail ? ` of ${e.detail}` : ""} to coordinators`,
  comparison_unshared: (e) => `stopped sending the comparison${e.detail ? ` of ${e.detail}` : ""} to coordinators`,
  sponsor_check: (e) => `checked the changes against the sponsor's summary${e.version ? ` for Version ${e.version}` : ""}${e.detail ? ` (${e.detail})` : ""}`,
  protocol_read: (e) => `started reading ${e.version ? `Version ${e.version}` : "a protocol"}${e.detail ? `: ${e.detail}` : ""}`,
  checklist_checked: (e) => `checked the Screening Visit checklist against Version ${e.version}: still right`,
  checklist_rebuild: (e) => `started rebuilding the Screening Visit checklist from Version ${e.version}`,
  visit_checked: (e) => `checked ${e.visit || "a visit"} against Version ${e.version}`,
  visit_snoozed: (e) => `snoozed the new-protocol reminder for ${e.visit || "a visit"}`,
  rebuild_override: (e) => `rebuilt source documents that were already built${e.detail ? ` (${e.detail})` : ""}`,
};

/** The study's protocol history, newest first. Nothing in it can be edited. */
const HistoryList = ({ events }) => {
  if (!events.length) return <div className={`${PANEL} p-4 text-sm text-slate-400`}>Nothing recorded yet.</div>;
  return (
    <ul className={`${PANEL} divide-y divide-white/5`}>
      {[...events].reverse().map((e) => (
        <li key={e.id} className="px-3 py-2 text-[12px] text-slate-300">
          <span className="text-slate-500">{when(e.at)}</span> · <span className="text-slate-200">{e.by || "RegX"}</span>{" "}
          {(EVENT_TEXT[e.type] || ((x) => x.type))(e)}
        </li>
      ))}
    </ul>
  );
};

/**
 * A pending "Yes, this is a new version" from another staff member: an admin
 * approves it as the next version or says it isn't one.
 */
const RequestPrompt = ({ request, next, onApprove, onDecline, onLater, busy }) =>
  createPortal(
    <div className="fixed inset-0 z-[85] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 font-[Manrope]">
      <div role="dialog" aria-modal="true" aria-label="A new protocol was sent for approval" className="protocol-version-dialog w-full max-w-md rounded-2xl border border-white/10 bg-[#0c0826]/95 p-5 text-slate-100">
        <h2 className="text-base font-semibold text-white mb-2">A new protocol was sent for approval</h2>
        <p className="text-sm text-slate-300">
          {request.requestedBy || "A staff member"} uploaded {request.fileName ? <strong className="text-white">{request.fileName}</strong> : "a protocol"}
          {request.source === "sourcex" ? " in SourceX" : ""}
          {request.requestedAt ? ` on ${when(request.requestedAt)}` : ""} and said it is a new version.
        </p>
        <p className="text-sm text-slate-300 mt-2">Approve it as Version {next}?</p>
        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button type="button" onClick={onLater} disabled={busy} className="protocol-version-soft-btn px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-200">Decide later</button>
          <button type="button" onClick={onDecline} disabled={busy} className="protocol-version-soft-btn px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-200">Not a new version</button>
          <button type="button" onClick={onApprove} disabled={busy} className="protocol-version-solid-btn px-3 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-sm font-semibold text-white ring-1 ring-white/20">
            Approve as Version {next}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );

const SOURCE_NOTE = {
  existing: "the protocol in use when versions started",
  "study-page": "uploaded on the study page",
  "screening-card": "uploaded in Screening Visit",
  sourcex: "uploaded in SourceX",
};

/**
 * The study page's Protocol versions card (admins). Every upload asks "Is this
 * a new version?" when the study has a protocol; a yes makes it the current
 * version and compares it against the one before. Staff uploads sent as new
 * versions wait here for an admin.
 * @param {{study: object}} props
 */
const ProtocolCompareButton = ({ study }) => {
  const studyKey = studyKeyOf(study);
  const [protocols, setProtocols] = useState(null);
  const [versions, setVersions] = useState([]);
  const [requests, setRequests] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list"); // "list" | <comparison id>
  const [pending, setPending] = useState(null); // { file, current, next }
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [laterIds, setLaterIds] = useState([]);
  const [waitFor, setWaitFor] = useState(null); // version whose comparison was just started
  const [answer, setAnswer] = useState(null); // "Is this a new version?" answered before the file is chosen
  const [events, setEvents] = useState([]);
  const fileRef = useRef(null);
  const { alert: showAlert, confirm: askConfirm, dialog } = useDialog();

  useEffect(() => {
    if (!studyKey) return undefined;
    const offs = [
      listenProtocols(studyKey, setProtocols, () => setProtocols([])),
      listenVersions(studyKey, setVersions, () => setVersions([])),
      listenVersionRequests(studyKey, setRequests, () => setRequests([])),
      listenComparisons(studyKey, setComparisons, () => setComparisons([])),
    ];
    return () => offs.forEach((off) => off && off());
  }, [studyKey]);

  const sorted = useMemo(() => [...versions].sort((a, b) => b.version - a.version), [versions]);
  const current = currentVersion(versions);
  const openRequests = requests.filter((r) => r.status === "pending");
  const prompt = openRequests.find((r) => !laterIds.includes(r.id));
  const question = newVersionQuestion(versions, protocols || []);

  // "Compare" pressed: open the comparison as soon as the server has created it.
  // The history, read only while the pop-up is open.
  useEffect(() => {
    if (!open || !studyKey) return undefined;
    return listenProtocolEvents(studyKey, setEvents, () => setEvents([]));
  }, [open, studyKey]);

  useEffect(() => {
    if (waitFor == null) return;
    const v = versions.find((x) => x.version === waitFor);
    if (v && v.comparisonId) {
      setOpen(true);
      setView(v.comparisonId);
      setWaitFor(null);
    }
  }, [waitFor, versions]);

  if (!studyKey || !protocols) return null;

  const startCompare = (v) => {
    setError("");
    setNote("");
    setWaitFor(v.version);
    compareVersion(studyKey, v.version).catch((e) => {
      setWaitFor(null);
      setError(e.message || "Couldn't compare the versions.");
    });
  };

  const chooseFile = (file) => {
    setError("");
    setNote("");
    if (!file) return;
    if (!/\.pdf$/i.test(file.name || "") && file.type !== "application/pdf") return setError("The protocol must be a PDF.");
    if (file.size > MAX_PDF_BYTES) return setError("This PDF is over 40 MB, the most the page reader takes.");
    const a = answer;
    setAnswer(null);
    if (a) upload(file, a.yes, a.next);
    else upload(file, true, 1); // the study's first protocol: Version 1, no question
  };

  // "Upload new protocol": with a protocol already here, say which one (version,
  // who, where, when) and ask "new version?" BEFORE picking the file.
  const startUpload = () => {
    setError("");
    setNote("");
    setAnswer(null);
    if (question.ask) setPending({ current: question.current, next: question.next });
    else if (fileRef.current) fileRef.current.click();
  };
  const answerAndPick = (yes) => {
    setAnswer({ yes, next: pending.next });
    setPending(null);
    if (fileRef.current) fileRef.current.click();
  };

  // Stored like any protocol (SourceX sees it too); never read by the protocol reader here.
  const upload = async (file, yes, next) => {
    setPending(null);
    setBusy(true);
    try {
      const { protocolId } = await uploadAndCreateProtocol(studyKey, file);
      if (!yes) {
        setNote(`Saved as a re-upload of Version ${question.current}.`);
        return;
      }
      setNote(`Version ${next} added.`);
      addVersion({ studyKey, protocolId, source: "study-page" }).catch((e) => setError(e.message || "Couldn't add the version."));
    } catch (e) {
      setError(e.message || "Couldn't upload the protocol.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const approve = (r) => {
    setLaterIds((ids) => [...ids, r.id]);
    setNote(`Version ${question.next} added.`);
    addVersion({ studyKey, protocolId: r.protocolId, source: r.source || "sourcex", requestId: r.id }).catch((e) =>
      setError(e.message || "Couldn't approve the version.")
    );
  };
  const decline = (r) => {
    setLaterIds((ids) => [...ids, r.id]);
    declineVersionRequest(studyKey, r.id, r).catch((e) => setError(e.message || "Couldn't save that."));
  };

  const selected = comparisons.find((c) => c.id === view);
  const comparisonOf = (v) => comparisons.find((c) => c.id === v.comparisonId);
  const inUse = !current && protocols.length ? protocols[0] : null;

  // "Send comparison to coordinators": the comparison open now, else the
  // current version's. Coordinators then see it, read-only, on the Studies page.
  const curCmpForShare = current && current.version >= 2 ? comparisonOf(current) : null;
  const shareTarget =
    (selected && selected.status === "done" && selected) || (curCmpForShare && curCmpForShare.status === "done" && curCmpForShare) || null;
  const toggleShare = async (share) => {
    if (!shareTarget) return;
    const label = shareTarget.new?.version ? `Version ${shareTarget.new.version} with Version ${shareTarget.old?.version}` : "this comparison";
    const ok = await askConfirm(
      share
        ? `Send the comparison of ${label} to coordinators? They'll see the AI summary and the redline on the Studies page, read-only, and can download them.`
        : `Stop showing the comparison of ${label} to coordinators?`,
      { title: share ? "Send comparison to coordinators?" : "Stop sending to coordinators?", confirmLabel: share ? "Send" : "Stop sending" }
    );
    if (!ok) return;
    try {
      await shareComparison({ study, studyKey, comparison: shareTarget, share });
      setNote(share ? `Sent the comparison of ${label} to coordinators.` : "Coordinators no longer see that comparison.");
    } catch (e) {
      showAlert(e.message || "Couldn't save that.");
    }
  };
  const shareButton = shareTarget ? (
    shareTarget.sharedWithCoordinators ? (
      <span className="flex items-center gap-1.5 shrink-0">
        <span className="protocol-version-current inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border font-semibold">
          <CheckCircle2 className="w-3.5 h-3.5" /> Sent to coordinators
        </span>
        <button type="button" onClick={() => toggleShare(false)} className={BTN_SOFT}>Stop sending</button>
      </span>
    ) : (
      <button type="button" onClick={() => toggleShare(true)} className={`${BTN_PRIMARY} shrink-0`}>
        <Send className="w-4 h-4" /> Send comparison to coordinators
      </button>
    )
  ) : (
    <button type="button" disabled title="Compare two versions first" className={`${BTN_SOFT} shrink-0`}>
      <Send className="w-3.5 h-3.5" /> Send comparison to coordinators
    </button>
  );
  // The card's main action: compare the current version with the one before.
  const curCmp = current && current.version >= 2 ? comparisonOf(current) : null;
  const running = (current && waitFor === current.version) || (curCmp && ["reading", "comparing", "summarizing"].includes(curCmp.status));

  return (
    <>
      <div className="protocol-compare-bar mt-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <GitCompare className="w-4 h-4 text-purple-300" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-200 font-medium">Protocol versions</div>
            <div className="text-[12px] text-slate-400 truncate">
              {current
                ? `Current: Version ${current.version}${current.fileName ? ` · ${current.fileName}` : ""}`
                : inUse
                  ? "The protocol in use becomes Version 1 when a new version is uploaded."
                  : "No protocol yet. The first upload is Version 1."}
            </div>
          </div>
          {protocols.length > 0 && (
            <button type="button" onClick={() => { setOpen(true); setView(sorted.length ? "list" : "uploads"); }} className={BTN_SOFT}>See protocols</button>
          )}
          <button type="button" onClick={startUpload} disabled={busy} className={BTN_SOFT}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload new protocol
          </button>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" aria-label="Protocol PDF" onChange={(e) => chooseFile(e.target.files?.[0] || null)} />
        </div>
        {current && current.version >= 2 && (
          <div className="mt-3">
            {running ? (
              <div className={`${PANEL} px-4 py-3 text-sm text-slate-200 flex items-center gap-2`} role="status">
                <Loader2 className="w-4 h-4 animate-spin text-purple-300" /> Comparing Version {current.version} with Version {current.version - 1}… You can leave; it keeps going.
              </div>
            ) : curCmp && curCmp.status === "done" ? (
              <button type="button" onClick={() => { setOpen(true); setView(curCmp.id); }} className={`${BTN_PRIMARY} w-full sm:w-auto px-6 py-3 text-base`}>
                <GitCompare className="w-5 h-5" /> See what changed: Version {current.version} vs Version {current.version - 1}
              </button>
            ) : curCmp && curCmp.status === "failed" ? (
              <div className="protocol-compare-error rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200 flex flex-wrap items-center gap-2">
                <span className="flex-1">The comparison didn&apos;t finish: {curCmp.errorMessage || "unknown error"}</span>
                <button type="button" onClick={() => retryComparison(studyKey, curCmp.id).catch((e) => setError(e.message))} className={BTN_SOFT}><RefreshCw className="w-3.5 h-3.5" /> Try again</button>
              </div>
            ) : (
              <button type="button" onClick={() => startCompare(current)} className={`${BTN_PRIMARY} w-full sm:w-auto px-6 py-3 text-base`}>
                <GitCompare className="w-5 h-5" /> Compare Version {current.version} with Version {current.version - 1}
              </button>
            )}
          </div>
        )}
        {openRequests.length > 0 && (
          <div className="protocol-version-request mt-2 rounded-lg border px-3 py-2 text-[12px] text-amber-200 flex flex-wrap items-center gap-2">
            <span className="flex-1">{openRequests.length} protocol(s) sent as a new version, waiting for you.</span>
            <button type="button" onClick={() => setLaterIds([])} className={BTN_SOFT}>Review</button>
          </div>
        )}
        {note && <div className="mt-2 text-[12px] text-slate-300" role="status">{note}</div>}
        {error && <div role="alert" className="protocol-compare-error mt-2 rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{error}</div>}
      </div>

      <NewVersionDialog
        open={!!pending}
        current={pending?.current}
        next={pending?.next}
        existing={describeCurrentProtocol(currentProtocolInfo(versions, protocols))}
        isAdmin
        onYes={() => answerAndPick(true)}
        onNo={() => answerAndPick(false)}
        onCancel={() => setPending(null)}
      />
      {prompt && !pending && (
        <RequestPrompt request={prompt} next={nextVersionFor(versions, protocols, prompt.protocolId)} busy={busy} onApprove={() => approve(prompt)} onDecline={() => decline(prompt)} onLater={() => setLaterIds((ids) => [...ids, prompt.id])} />
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Protocol versions" subtitle={study?.NAME || studyKey} headerAction={shareButton}>
        {["list", "uploads", "history"].includes(view) && (
          <div role="tablist" className="mb-3 inline-flex rounded-xl border border-white/10 overflow-hidden">
            {[["list", "Versions"], ["uploads", "All uploads"], ["history", "History"]].map(([id, label]) => (
              <button key={id} role="tab" type="button" aria-selected={view === id} onClick={() => setView(id)} className={`protocol-compare-tab ${view === id ? "protocol-compare-tab--on bg-purple-500/20 text-white" : "bg-white/[0.03] text-slate-300"} px-3 py-1.5 text-xs font-semibold`}>
                {label}
              </button>
            ))}
          </div>
        )}
        {view === "uploads" && <UploadsList protocols={protocols} versions={versions} requests={requests} />}
        {view === "history" && <HistoryList events={events} />}
        {view === "list" && !sorted.length && (
          <div className={`${PANEL} p-4 text-sm text-slate-400`}>No versions yet. The protocol in use becomes Version 1 when a new version is uploaded.</div>
        )}
        {view === "list" && (
          <ul className="space-y-2">
            {sorted.map((v) => {
              const c = comparisonOf(v);
              return (
                <li key={v.id} className={`${PANEL} p-3`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-white font-semibold">Version {v.version}</span>
                    {current && v.version === current.version && <span className="protocol-version-current text-[10px] px-1.5 py-0.5 rounded border font-semibold">Current</span>}
                    <span className="text-[12px] text-slate-300 truncate">{v.fileName}</span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {SOURCE_NOTE[v.source] || ""}
                    {v.requestedBy ? ` by ${v.requestedBy}, approved by ${v.addedBy}` : v.addedBy ? ` · added by ${v.addedBy}` : ""}
                    {v.addedAt ? ` · ${when(v.addedAt)}` : ""}
                  </div>
                  {v.version >= 2 && !v.comparisonId && (
                    <button type="button" onClick={() => startCompare(v)} disabled={waitFor === v.version} className={`${BTN_PRIMARY} mt-2`}>
                      {waitFor === v.version ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompare className="w-4 h-4" />} Compare with Version {v.version - 1}
                    </button>
                  )}
                  {v.comparisonId && (
                    <button type="button" onClick={() => setView(v.comparisonId)} className={`${BTN_SOFT} mt-2`}>
                      Compared against Version {v.previousVersion}
                      {c ? ` · ${c.status === "done" ? `${(c.stats && c.stats.hunks) || 0} change(s)` : STATUS_CHIP[c.status] ? STATUS_CHIP[c.status][0] : c.status}` : ""}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {!["list", "uploads", "history"].includes(view) && (
          selected ? (
            <ResultView study={study} studyKey={studyKey} comparison={selected} onBack={() => setView("list")} askConfirm={askConfirm} showAlert={showAlert} />
          ) : (
            <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Starting…</div>
          )
        )}
      </Modal>
      {createPortal(dialog, document.body)}
    </>
  );
};

/**
 * Coordinators (Studies page): a comparison an admin sent, read-only: the AI
 * summary (with the AI warning) and the redline, both downloadable. Nothing
 * can be regenerated, compared or uploaded here.
 * @param {{study: object, pointer: {studyKey:string, comparisonId:string}, onClose: () => void}} props
 */
export const SharedComparisonModal = ({ study, pointer, onClose }) => {
  const [comparison, setComparison] = useState(null);
  const [error, setError] = useState("");
  const { alert: showAlert, confirm: askConfirm, dialog } = useDialog();
  const studyKey = pointer?.studyKey || studyKeyOf(study);
  useEffect(() => {
    if (!pointer?.comparisonId || !studyKey) return undefined;
    return listenComparison(studyKey, pointer.comparisonId, (c) => {
      setComparison(c);
      setError(c && c.sharedWithCoordinators === false ? "This comparison is no longer shared." : "");
    }, () => setError("This comparison isn't available."));
  }, [pointer?.comparisonId, studyKey]);
  const subtitle = pointer?.newVersion ? `${study?.NAME || studyKey} · Version ${pointer.newVersion} vs Version ${pointer.oldVersion}` : study?.NAME || studyKey;
  return (
    <>
      <Modal open onClose={onClose} title="Protocol changes" subtitle={subtitle}>
        {error && <div className={`${PANEL} p-4 text-sm text-slate-300`}>{error}</div>}
        {!error && !comparison && <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
        {!error && comparison && (
          <ResultView readOnly study={study} studyKey={studyKey} comparison={comparison} onBack={onClose} askConfirm={askConfirm} showAlert={showAlert} />
        )}
      </Modal>
      {createPortal(dialog, document.body)}
    </>
  );
};

export default ProtocolCompareButton;
